import { useCallback, useEffect, useRef, useState } from 'react';
import {
    DEFAULT_SIDEBAR_WIDTH,
    SIDEBAR_MAX_WIDTH,
    SIDEBAR_MIN_WIDTH,
    STORAGE_KEYS,
} from '../config/modes';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const readStoredWidth = (storageKey = STORAGE_KEYS.SIDEBAR_WIDTH) => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(storageKey);
        const parsed = raw ? Number(raw) : NaN;
        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const writeStoredWidth = (width, storageKey = STORAGE_KEYS.SIDEBAR_WIDTH) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(storageKey, String(width));
    } catch {
        // ignore
    }
};

const getViewportMaxWidth = () => {
    if (typeof window === 'undefined') return SIDEBAR_MAX_WIDTH;
    return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, window.innerWidth - 120));
};

export function useSidebarWidth({ side = 'right', defaultWidth = DEFAULT_SIDEBAR_WIDTH, storageKey = STORAGE_KEYS.SIDEBAR_WIDTH } = {}) {
    const [width, setWidth] = useState(() => {
        const stored = readStoredWidth(storageKey);
        const base = stored ?? defaultWidth;
        return clamp(base, SIDEBAR_MIN_WIDTH, getViewportMaxWidth());
    });

    const widthRef = useRef(width);
    widthRef.current = width;

    useEffect(() => {
        writeStoredWidth(width, storageKey);
    }, [width, storageKey]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const onResize = () => {
            setWidth((prev) => clamp(prev, SIDEBAR_MIN_WIDTH, getViewportMaxWidth()));
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const startResize = useCallback(
        (event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            const startX = event.clientX;
            const startWidth = widthRef.current;
            const direction = side === 'right' ? -1 : 1;

            const onMove = (ev) => {
                const delta = (ev.clientX - startX) * direction;
                setWidth(clamp(startWidth + delta, SIDEBAR_MIN_WIDTH, getViewportMaxWidth()));
            };

            const onUp = () => {
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
                document.body.style.userSelect = '';
            };

            document.body.style.userSelect = 'none';
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
        },
        [side],
    );

    return { width, setWidth, startResize };
}
